import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, Leaf, MapPin } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { naturalCompare } from "@/lib/sorting";

interface Block {
  id: string;
  name: string;
  greenhouse_id: string;
  area_m2: number | null;
}

interface Greenhouse {
  id: string;
  name: string;
  location_id: string | null;
}

interface CycleInfo {
  crop_name?: string;
  variety_name?: string;
}

export interface GreenhouseSelection {
  greenhouse_id: string;
  greenhouse_name: string;
  block_ids: string[];
}

interface MultiGreenhouseSelectorProps {
  greenhouses: Greenhouse[];
  blocksByGreenhouse: Map<string, Block[]>;
  blockCyclesMap: Map<string, CycleInfo | null>;
  selections: GreenhouseSelection[];
  onSelectionsChange: (selections: GreenhouseSelection[]) => void;
  disabled?: boolean;
  formatCycleLabel: (cycle: CycleInfo) => string;
  locationFilter?: string;
  isEditing?: boolean;
}

export default function MultiGreenhouseSelector({
  greenhouses,
  blocksByGreenhouse,
  blockCyclesMap,
  selections,
  onSelectionsChange,
  disabled = false,
  formatCycleLabel,
  locationFilter,
  isEditing = false,
}: MultiGreenhouseSelectorProps) {
  const { t } = useTranslation();
  const [isMultiMode, setIsMultiMode] = useState(!isEditing && selections.length > 1);
  // Acordeón: solo una nave abierta a la vez. Abrir otra cierra la anterior y la
  // ventana no se dispara a lo alto. Cerrar NO deselecciona nada — la selección vive
  // en `selections`, aquí solo se guarda cuál se está mirando.
  const [expandedGreenhouse, setExpandedGreenhouse] = useState<string | null>(null);

  // Por nombre, y natural: así «Nave 2» va antes que «Nave 10», que es como lo lee
  // cualquiera. Llegaban en el orden en que los creó la empresa, que no es ninguno.
  const filteredGreenhouses = useMemo(() => {
    const list = locationFilter && locationFilter !== "all"
      ? greenhouses.filter(gh => gh.location_id === locationFilter)
      : greenhouses;
    return [...list].sort((a, b) => naturalCompare(a.name || "", b.name || ""));
  }, [greenhouses, locationFilter]);

  useEffect(() => {
    // Al abrir, se despliega la primera nave que ya tuviera sectores elegidos.
    if (selections.length > 0) setExpandedGreenhouse(selections[0].greenhouse_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGreenhouseExpanded = (ghId: string) => {
    setExpandedGreenhouse(prev => (prev === ghId ? null : ghId));
  };

  /**
   * Sectores de una nave, en orden natural (A, B, C… y «Sector 2» antes que
   * «Sector 10»). Se ordena AQUÍ y no solo al pintar, para que «Seleccionar todas»
   * y el conteo trabajen sobre exactamente la misma lista que se ve.
   */
  const blocksOf = (ghId: string) =>
    [...(blocksByGreenhouse.get(ghId) || [])].sort((a, b) =>
      naturalCompare(a.name || "", b.name || ""),
    );

  const isGreenhouseSelected = (ghId: string) =>
    selections.some(s => s.greenhouse_id === ghId);

  const isBlockSelected = (ghId: string, blockId: string) => {
    const sel = selections.find(s => s.greenhouse_id === ghId);
    return sel ? sel.block_ids.includes(blockId) : false;
  };

  const handleBlockToggle = (gh: Greenhouse, blockId: string, checked: boolean) => {
    const existingSelection = selections.find(s => s.greenhouse_id === gh.id);

    if (!existingSelection) {
      if (checked) {
        const next = {
          greenhouse_id: gh.id,
          greenhouse_name: gh.name,
          block_ids: [blockId],
        };
        if (!isMultiMode) onSelectionsChange([next]);
        else onSelectionsChange([...selections, next]);
      }
    } else {
      const newBlockIds = checked
        ? [...existingSelection.block_ids, blockId]
        : existingSelection.block_ids.filter(id => id !== blockId);
      // Drop greenhouse entirely if no blocks left
      if (newBlockIds.length === 0) {
        onSelectionsChange(selections.filter(s => s.greenhouse_id !== gh.id));
      } else {
        onSelectionsChange(
          selections.map(s =>
            s.greenhouse_id === gh.id ? { ...s, block_ids: newBlockIds } : s,
          ),
        );
      }
    }
  };

  const handleSelectAllBlocks = (gh: Greenhouse) => {
    const blocks = blocksOf(gh.id);
    const existingSelection = selections.find(s => s.greenhouse_id === gh.id);
    if (!existingSelection) {
      const newSelection: GreenhouseSelection = {
        greenhouse_id: gh.id,
        greenhouse_name: gh.name,
        block_ids: blocks.map(b => b.id),
      };
      if (!isMultiMode) onSelectionsChange([newSelection]);
      else onSelectionsChange([...selections, newSelection]);
    } else {
      const allSelected = blocks.every(b => existingSelection.block_ids.includes(b.id));
      const newBlockIds = allSelected ? [] : blocks.map(b => b.id);
      if (newBlockIds.length === 0) {
        onSelectionsChange(selections.filter(s => s.greenhouse_id !== gh.id));
      } else {
        onSelectionsChange(
          selections.map(s =>
            s.greenhouse_id === gh.id ? { ...s, block_ids: newBlockIds } : s,
          ),
        );
      }
    }
  };

  const handleMultiModeChange = (enabled: boolean) => {
    setIsMultiMode(enabled);
    if (!enabled && selections.length > 1) {
      onSelectionsChange([selections[0]]);
    }
  };

  return (
    <div className="space-y-4">
      {/* Multi-mode toggle */}
      <div className="flex items-center justify-between p-3 rounded-2xl border border-[#E4EDF5] bg-white shadow-[0_4px_16px_rgba(20,60,100,0.05)]">
        <div className="min-w-0">
          <Label htmlFor="multi-mode" className="text-sm font-semibold text-foreground">
            {t("planner.multipleGreenhouses")}
          </Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("planner.multiGreenhouseHint", "Trata varias naves en una tarea")}
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={isEditing ? "opacity-50 cursor-not-allowed" : ""}>
                <Switch
                  id="multi-mode"
                  checked={isEditing ? false : isMultiMode}
                  onCheckedChange={handleMultiModeChange}
                  disabled={disabled || isEditing}
                  className={cn(
                    "data-[state=checked]:bg-teal-500",
                    isEditing && "pointer-events-none",
                  )}
                />
              </div>
            </TooltipTrigger>
            {isEditing && (
              <TooltipContent>
                <p>{t("planner.multiGreenhouseNotAvailableEditing")}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>

      {filteredGreenhouses.length === 0 ? (
        <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-lg">
          {t("planner.noGreenhousesInLocation")}
        </p>
      ) : (
        <div className="space-y-3">
          {filteredGreenhouses.map(gh => {
            const blocks = blocksOf(gh.id);
            const isExpanded = expandedGreenhouse === gh.id;
            const isSelected = isGreenhouseSelected(gh.id);
            const selection = selections.find(s => s.greenhouse_id === gh.id);
            const selectedBlockCount = selection?.block_ids.length || 0;

            // Aggregate cycle: pick the first non-empty cycle on selected blocks
            // (fallback to first non-empty cycle on any block) for the header chip.
            const cyclesForCard: CycleInfo[] = blocks
              .map(b => blockCyclesMap.get(b.id))
              .filter((c): c is CycleInfo => !!c);
            const cycleChip = cyclesForCard[0];

            const selectedAreaHa = (selection?.block_ids || []).reduce((sum, id) => {
              const b = blocks.find(bl => bl.id === id);
              return sum + (b?.area_m2 ? b.area_m2 / 10000 : 0);
            }, 0);

            const allSelected =
              blocks.length > 0 && blocks.every(b => isBlockSelected(gh.id, b.id));

            return (
              <Collapsible
                key={gh.id}
                open={isExpanded}
                onOpenChange={() => toggleGreenhouseExpanded(gh.id)}
              >
                <div
                  className={cn(
                    "rounded-2xl border bg-white shadow-sm transition-colors overflow-hidden",
                    isSelected
                      ? "border-[#16A971] ring-1 ring-[#16A971]/25"
                      : "border-[#E4EDF5]",
                  )}
                >
                  {/* Header row */}
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-muted/30 transition-colors"
                      disabled={disabled}
                    >
                      {/* Selection indicator */}
                      <div
                        className={cn(
                          "shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-colors",
                          isSelected
                            ? "bg-teal-500 text-white"
                            : "border border-[#E4EDF5] bg-white",
                        )}
                        aria-hidden
                      >
                        {isSelected ? (
                          selectedBlockCount === blocks.length && blocks.length > 0 ? (
                            <Check className="w-3.5 h-3.5" strokeWidth={3} />
                          ) : (
                            <span className="w-2 h-0.5 bg-white rounded" />
                          )
                        ) : null}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-foreground text-base">
                            {gh.name}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            · {blocks.length} {t("planner.summary.secciones", "secciones")}
                          </span>
                        </div>
                        {isSelected && selectedBlockCount > 0 ? (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs font-semibold text-teal-600">
                              {selectedBlockCount} sec ·{" "}
                              {selectedAreaHa.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              ha
                            </span>
                            {cycleChip && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-medium">
                                <Leaf className="w-2.5 h-2.5" />
                                {formatCycleLabel(cycleChip)}
                              </span>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {t("planner.tapToChooseSections", "Toca para elegir secciones")}
                          </p>
                        )}
                      </div>

                      <ChevronDown
                        className={cn(
                          "w-4 h-4 text-muted-foreground shrink-0 transition-transform",
                          isExpanded && "rotate-180",
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    {blocks.length === 0 ? (
                      <p className="text-xs text-muted-foreground px-4 pb-4">
                        {t("planner.noBlocksInGreenhouse")}
                      </p>
                    ) : (
                      <div className="px-3 pb-4 space-y-3">
                        {/* Select all */}
                        <button
                          type="button"
                          onClick={() => handleSelectAllBlocks(gh)}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-600 hover:text-teal-700 px-1"
                          disabled={disabled}
                        >
                          <Check className="w-3.5 h-3.5" strokeWidth={3} />
                          {allSelected ? t("common.deselectAll") : t("planner.selectAllSections", "Seleccionar todas")}
                        </button>

                        {/* Sectores de tres en tres: caben, y la ventana deja de ser
                            una columna interminable. La etiqueta del cultivo se queda
                            debajo del nombre, que es donde hay sitio. */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                          {blocks.map(block => {
                            const selected = isBlockSelected(gh.id, block.id);
                            const cycle = blockCyclesMap.get(block.id);
                            const ha = block.area_m2 ? block.area_m2 / 10000 : null;
                            return (
                              <button
                                key={block.id}
                                type="button"
                                onClick={() => handleBlockToggle(gh, block.id, !selected)}
                                disabled={disabled}
                                className={cn(
                                  "min-w-0 text-left rounded-xl border p-2 transition-colors flex items-start gap-1.5",
                                  selected
                                    ? "border-[#16A971] bg-[#16A971]/10 ring-1 ring-[#16A971]/25"
                                    : "border-[#E4EDF5] bg-white hover:bg-muted/30",
                                )}
                              >
                                <div
                                  className={cn(
                                    "shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center mt-px",
                                    selected
                                      ? "bg-[#16A971] text-white"
                                      : "border border-[#E4EDF5] bg-white",
                                  )}
                                  aria-hidden
                                >
                                  {selected && (
                                    <Check className="w-2.5 h-2.5" strokeWidth={3} />
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  {/* Line 1: name + size */}
                                  <div className="flex items-baseline gap-1 flex-wrap">
                                    <span className="font-semibold text-foreground text-[13px] leading-tight">
                                      {block.name}
                                    </span>
                                    {ha != null && (
                                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                        {ha.toLocaleString(undefined, {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}{" "}
                                        ha
                                      </span>
                                    )}
                                  </div>
                                  {/* Line 2: crop badge */}
                                  {cycle && (
                                    <span className="inline-flex items-center gap-0.5 mt-1 px-1 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-medium max-w-full">
                                      <Leaf className="w-2 h-2 shrink-0" />
                                      <span className="truncate">
                                        {formatCycleLabel(cycle)}
                                      </span>
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      )}

      {/* Multi-greenhouse note */}
      {isMultiMode && selections.length > 1 && (
        <div className="p-2.5 bg-primary/10 rounded-xl border border-primary/20 flex items-start gap-2">
          <MapPin className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-primary">
            {t("planner.tasksWillBeCreatedPerGreenhouse", { count: selections.length })}
          </p>
        </div>
      )}
    </div>
  );
}
